/**
 * Timeline row projection (ticket 3525, upstream pin e3e7cc3f, MIT), split
 * out of the ported `MessagesTimeline.logic.ts`: keeps one projection per
 * timeline and reuses rows only when streaming content changes, plus the
 * user-message-to-checkpoint revert matching.
 *
 * @module MessagesTimeline.projection
 */

import { shallow } from "zustand/vanilla/shallow";
import type { MessageId, TurnId } from "@cmmd-forge/contracts";
import { isStreamingMessageTextUpdate, type TimelineEntry } from "../../session-logic";
import type { ChatMessage, TurnDiffSummary } from "../../types";
import type { MessagesTimelineRow, TimelineLatestTurn } from "./MessagesTimeline.rowTypes";

export interface MessagesTimelineRowsInput {
  readonly timelineEntries: ReadonlyArray<TimelineEntry>;
  readonly latestTurn?: TimelineLatestTurn | null;
  readonly runningTurnId?: TurnId | null;
  readonly expandedTurnIds?: ReadonlySet<TurnId>;
  readonly expandedWorkGroupIds?: ReadonlySet<string>;
  readonly isWorking: boolean;
  readonly activeTurnStartedAt: string | null;
  readonly turnDiffSummaries: ReadonlyArray<TurnDiffSummary>;
  readonly supportsConversationRollback: boolean;
  /** Task ids of subagents still working, used by the active tool indicator. */
  readonly liveAgentTaskIds?: ReadonlySet<string> | undefined;
}

export interface MessagesTimelineRowsProjection {
  readonly input: MessagesTimelineRowsInput;
  readonly rows: MessagesTimelineRow[];
}

// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function replaceStreamingMessageRows(
  input: MessagesTimelineRowsInput,
  previous: MessagesTimelineRowsProjection,
): MessagesTimelineRow[] | null {
  const {
    timelineEntries: previousEntries,
    turnDiffSummaries: previousSummaries,
    latestTurn: previousLatestTurn,
    expandedTurnIds: previousExpandedTurns,
    expandedWorkGroupIds: previousExpandedGroups,
    ...previousContext
  } = previous.input;
  const {
    timelineEntries,
    turnDiffSummaries,
    latestTurn,
    expandedTurnIds,
    expandedWorkGroupIds,
    ...context
  } = input;
  if (
    timelineEntries.length !== previousEntries.length ||
    !shallow(previousContext, context) ||
    !shallow(previousSummaries, turnDiffSummaries) ||
    !shallow(previousLatestTurn, latestTurn) ||
    !shallow(previousExpandedTurns, expandedTurnIds) ||
    !shallow(previousExpandedGroups, expandedWorkGroupIds)
  ) {
    return null;
  }
  const replacements = new Map<ChatMessage, ChatMessage>();
  for (const [index, entry] of timelineEntries.entries()) {
    const previousEntry = previousEntries[index]!;
    if (entry === previousEntry) continue;
    if (
      entry.kind !== "message" ||
      previousEntry.kind !== "message" ||
      entry.id !== previousEntry.id ||
      entry.createdAt !== previousEntry.createdAt
    ) {
      return null;
    }
    if (entry.message === previousEntry.message) continue;
    if (!isStreamingMessageTextUpdate(previousEntry.message, entry.message)) return null;
    replacements.set(previousEntry.message, entry.message);
  }
  if (replacements.size === 0) return previous.rows;
  return previous.rows.map((row) => {
    if (row.kind === "activity-group") {
      if (!row.entries.some((entry) => entry.kind === "message" && replacements.has(entry.message)))
        return row;
      return {
        ...row,
        entries: row.entries.map((entry) => {
          if (entry.kind !== "message") return entry;
          const message = replacements.get(entry.message);
          return message ? { ...entry, message } : entry;
        }),
      };
    }
    if (row.kind !== "message" && row.kind !== "assistant-meta") return row;
    const message = replacements.get(row.message);
    return message ? { ...row, message } : row;
  });
}

/** Keep one projection per timeline. Reuse rows only when streaming content changes. */
export function projectMessagesTimelineRows(
  input: MessagesTimelineRowsInput,
  previous: MessagesTimelineRowsProjection | null,
  derive: (input: MessagesTimelineRowsInput) => MessagesTimelineRow[],
): MessagesTimelineRowsProjection {
  return {
    input,
    rows:
      (previous === null ? null : replaceStreamingMessageRows(input, previous)) ?? derive(input),
  };
}

/** Match each user message to the next assistant checkpoint. */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function buildRevertTurnCountByUserMessageId(input: {
  supportsConversationRollback: boolean;
  timelineEntries: ReadonlyArray<TimelineEntry>;
  turnDiffSummaryByAssistantMessageId: ReadonlyMap<MessageId, TurnDiffSummary>;
  inferredCheckpointTurnCountByTurnId: Readonly<Record<string, number | undefined>>;
}): Map<MessageId, number> {
  const byUserMessageId = new Map<MessageId, number>();
  const entryCount = input.supportsConversationRollback ? input.timelineEntries.length : 0;
  for (let index = 0; index < entryCount; index += 1) {
    const entry = input.timelineEntries[index];
    if (!entry || entry.kind !== "message" || entry.message.role !== "user") {
      continue;
    }

    for (let nextIndex = index + 1; nextIndex < input.timelineEntries.length; nextIndex += 1) {
      const nextEntry = input.timelineEntries[nextIndex];
      if (!nextEntry || nextEntry.kind !== "message") {
        continue;
      }
      if (nextEntry.message.role === "user") {
        break;
      }
      const summary = input.turnDiffSummaryByAssistantMessageId.get(nextEntry.message.id);
      if (!summary) {
        continue;
      }
      const turnCount =
        summary.checkpointTurnCount ?? input.inferredCheckpointTurnCountByTurnId[summary.turnId];
      if (typeof turnCount !== "number") {
        break;
      }
      byUserMessageId.set(entry.message.id, Math.max(0, turnCount - 1));
      break;
    }
  }
  return byUserMessageId;
}
